import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import pb from './pb';
import { seedTasksIfNeeded } from './seedTasks';
import "./chores.css";

const MEMBERS = ['DB','NM','LB','HB'];
// const CURRENT_USER = 'DB'; // set this however you already identify the user
// --- Per-device identity helpers (who's using this device) ---
const FAMILY = ['DB', 'NM', 'LB', 'HB']; // keep in sync with PB `users.initials`

function loadMe() {
  try {
    return localStorage.getItem('fridge:userInitials') || FAMILY[0];
  } catch {
    return FAMILY[0];
  }
}

function saveMe(v) {
  try {
    localStorage.setItem('fridge:userInitials', v);
  } catch {}
}




// period helpers (Mon-start week)
function isoWeekInfo(d=new Date()){
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = (x.getUTCDay()+6)%7; // Mon=0
  x.setUTCDate(x.getUTCDate()-day+3);
  const week1 = new Date(Date.UTC(x.getUTCFullYear(),0,4));
  const week = 1+Math.round(((x-week1)/86400000-3+((week1.getUTCDay()+6)%7))/7);
  return { year: x.getUTCFullYear(), week };
}
const weekKey = (d=new Date()) => {
  const {year,week} = isoWeekInfo(d);
  return `${year}-W${String(week).padStart(2,'0')}`;
};
const fortnightKey = (d=new Date()) => {
  const {year,week} = isoWeekInfo(d);
  return `${year}-FN${String(Math.ceil(week/2)).padStart(2,'0')}`;
};
const quarterKey = (d=new Date()) => {
  const q = Math.floor(d.getMonth()/3)+1;
  return `${d.getFullYear()}-Q${q}`;
};
const todayKey = (d=new Date()) => d.toISOString().slice(0,10); // YYYY-MM-DD

function scopeAndKey(freq){
  if (freq==='daily') return {scope:'daily', key:todayKey()};
  if (freq==='weekly') return {scope:'weekly', key:weekKey()};
  if (freq==='fortnightly') return {scope:'fortnightly', key:fortnightKey()};
  if (freq==='quarterly') return {scope:'quarterly', key:quarterKey()};
  if (freq==='monthly') return {scope:'monthly', key:`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`};
  return {scope:'weekly', key:weekKey()};
}

/* ---------- Layout styles ---------- */
const frame = {
  minHeight: '100vh',
  display: 'grid',
  gap: 12,
  padding: 12,
  // 2 columns, chores takes the whole right column
  gridTemplateColumns: 'minmax(340px, 1fr) minmax(480px, 1.35fr)',
  gridTemplateRows: 'minmax(320px, auto) minmax(320px, 1fr)',
  gridTemplateAreas: `
    "shopping chores"
    "leftovers chores"
  `,
};
const panel = (border) => ({
  border: `3px solid ${border}`, borderRadius: 10, padding: 14, position: 'relative',
});
const areas = {
  shopping: { gridArea: 'shopping', ...panel('#1e63ff') },
  leftovers:{ gridArea: 'leftovers', ...panel('#c64bf5') },
  chores:   { gridArea: 'chores',   ...panel('#55a630') },
};

// mobile: stack all, then optional archive button still lives in chores panel
const responsiveCSS = `
  @media (max-width: 900px) {
    .frameShell {
      grid-template-columns: 1fr;
      grid-template-rows: auto auto auto;
      grid-template-areas:
        "shopping"
        "leftovers"
        "chores";
    }
  }
`;

/* ---------- Modal ---------- */
function Modal({ open, onClose, title, children }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Dialog'}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'grid', placeItems: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1000px, 92vw)', maxHeight: '88vh',
          background: '#fff', borderRadius: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          display: 'grid', gridTemplateRows: 'auto 1fr', overflow: 'hidden',
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                         padding: '12px 16px', borderBottom: '1px solid #eee' }}>
          <strong style={{ fontSize: 18 }}>{title || 'Archive'}</strong>
          <button onClick={onClose} aria-label="Close" style={{ padding: '6px 10px' }}>Close</button>
        </header>
        <div style={{ overflow: 'auto', padding: 16 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ---------- Shared helpers ---------- */
const SHOPS = ['Supermarket', 'Butcher', 'Bakery', 'Greengrocer', 'Deli', 'Pharmacy'];
const MS_DAY = 86400000;
const daysBetween = (a, b) => Math.floor((b - a) / MS_DAY);
const daysAgoText = (iso) => {
  if (!iso) return '';
  const n = daysBetween(new Date(iso), new Date());
  if (n <= 0) return 'purchased today';
  if (n === 1) return 'purchased 1 day ago';
  return `purchased ${n} days ago`;
};



/* ---------- Shopping List mini‑app ---------- */
function ShoppingList() {
  console.log('[ShoppingList] render');
  const [items, setItems] = useState([]);
  const [nameInput, setName] = useState('');
  const [shopInput, setShop] = useState('Supermarket');
  const [status, setStatus] = useState('boot'); // boot/loading/ready/error/...
  const [, force] = useState(0);
const [showPurchased, setShowPurchased] = useState(false);
// who’s using this device?
const [me, setMe] = React.useState(loadMe());
const [editingUser, setEditingUser] = React.useState(false);
React.useEffect(() => { saveMe(me); }, [me]);
// Confirm modal state
const [confirmOpen, setConfirmOpen] = React.useState(false);

// Open → confirm/cancel
function openConfirmShoppingNow() {
  setConfirmOpen(true);
}
async function confirmShoppingNow() {
  try {
    await sendShoppingNow();   // uses your existing function that creates the PB event
  } finally {
    setConfirmOpen(false);
  }
}
function cancelShoppingNow() {
  setConfirmOpen(false);
}

async function sendShoppingNow() {
  try {
    await pb.collection('events').create({
      kind: 'shopping_now',
      created_by: (me || '').trim().toUpperCase(),
      payload: {}
    });
    alert('Sent “Shopping now” 👍');
  } catch (e) {
    console.error('[ShoppingList] shopping_now error', e, e?.data);
    alert('Failed to send “Shopping now” – see console for details.');
  }
}
  useEffect(() => {
    console.log('[ShoppingList] useEffect start');
    let unsub = () => {};
    (async () => {
      try {
        setStatus('loading…');
        const list = await pb.collection('shopping_items').getFullList({ sort: '-created' });
        console.log('[ShoppingList] loaded list count =', list.length);
        setItems(list);

        unsub = await pb.collection('shopping_items').subscribe('*', async (ev) => {
          console.log('[ShoppingList] realtime:', ev?.action, ev?.record?.id);
          const updated = await pb.collection('shopping_items').getFullList({ sort: '-created' });
          setItems(updated);
        });

        setStatus('ready');
      } catch (e) {
        console.error('[ShoppingList] init error:', e);
        setStatus('error: ' + (e?.message || String(e)));
      }
    })();

  

    return () => { try { unsub(); } catch {} };
  }, []);

 useEffect(() => {
    const tick = () => force(x => x + 1);

    // fire once when the tab becomes visible (e.g., in the morning)
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);

    // schedule next midnight
    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 0, 0);
    const ms = nextMidnight - now;

    // one-shot to next midnight, then daily
    const toMidnight = setTimeout(() => {
      tick();
      // keep it fresh each day even if the app stays open
      var daily = setInterval(tick, 24 * 60 * 60 * 1000);
      // stash on window so we can clear it if HMR restarts (optional)
      window.__shoppingDaily = daily;
    }, ms);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearTimeout(toMidnight);
      if (window.__shoppingDaily) { clearInterval(window.__shoppingDaily); delete window.__shoppingDaily; }
    };
  }, []);

  async function addItem() {
    const name = nameInput.trim();
    console.log('[ShoppingList] addItem clicked, name =', name, 'shop =', shopInput);
    if (!name) return;
    try {
      setStatus('creating…');
      const rec = await pb.collection('shopping_items').create({
        name,
        shop: shopInput,
        status: 'awaiting',
        createdAt: new Date().toISOString(),
        purchasedAt: null,
      });
      console.log('[ShoppingList] created id =', rec.id);
      setName('');
      setStatus('ready');
    } catch (e) {
      console.error('[ShoppingList] create error:', e);
      setStatus('error: ' + (e?.message || String(e)));
      alert('Failed to add item (see console).');
    }
  }

  async function removeItem(id) {
    console.log('[ShoppingList] delete', id);
    try { await pb.collection('shopping_items').delete(id); }
    catch (e) { console.error('[ShoppingList] delete error:', e); }
  }

  async function togglePurchased(item) {
    console.log('[ShoppingList] toggle', item.id, 'status =', item.status);
    try {
      if (item.status === 'awaiting') {
        await pb.collection('shopping_items').update(item.id, {
          status: 'purchased',
          purchasedAt: new Date().toISOString(),
        });
      } else {
        await pb.collection('shopping_items').update(item.id, {
          status: 'awaiting',
          purchasedAt: null,
        });
      }
    } catch (e) { console.error('[ShoppingList] toggle error:', e); }
  }

  const today = new Date();
  const awaiting = items.filter(i => i.status === 'awaiting')
.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt))
  const purchased = items.filter(i => i.status === 'purchased' && i.purchasedAt &&
                        Math.floor((today - new Date(i.purchasedAt))/86400000) <= 4)
                        .sort((a,b) => new Date(b.purchasedAt) - new Date(a.purchasedAt));
const sortedItems = [...awaiting, ...(showPurchased ? purchased : [])];
  return (
    <div>
     {/* header */}
<div
  style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
  }}
>
  <h2 style={{ margin: 0, color: '#1e63ff' }}>shopping list</h2>

  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
   <button
  onClick={openConfirmShoppingNow}
  style={{ padding: '6px 12px', borderRadius: 16, border: '2px solid #1e63ff', background: '#eef4ff' }}
  title="Let everyone know you’re at the shops"
>
  Shopping now
</button>

    {editingUser ? (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      color: '#666',
      border: '1px dashed #bbb',
      padding: '2px 6px',
      borderRadius: 8,
      background: '#fffff3',
    }}
  >
    I’m
    <select
      value={me}
      onChange={(e) => {
        const value = e.target.value;
        setMe(value);
        saveMe(value);
        setEditingUser(false); // hide picker after choosing
      }}
      style={{ padding: '4px 8px', borderRadius: 8 }}
      title="Who’s using this device?"
    >
      <option value="">-- pick --</option>
      {FAMILY.map((i) => (
        <option key={i} value={i}>
          {i}
        </option>
      ))}
    </select>
  </div>
) : (
  <span
    style={{
      fontSize: 12,
      color: '#666',
      cursor: 'pointer',
      userSelect: 'none',
      borderBottom: '1px dotted #aaa',
    }}
    title="Click to change user"
    onClick={() => setEditingUser(true)}
  >
    I’m {me}
  </span>
)}
  </div>
</div>

      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:8 }}>
        <input
          type="text"
          placeholder="Add an item (e.g., Milk)"
          value={nameInput}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' ? addItem() : null}
          style={{ flex:1, minWidth:220, padding:8 }}
        />
        <select value={shopInput} onChange={(e) => setShop(e.target.value)} style={{ padding:8 }}>
          {['Supermarket','Vic market','Bakery','Greengrocer','Roasters', 'other'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={addItem} style={{ padding:'8px 14px' }}>Add</button>
      </div>

      <div style={{ fontSize:12, color: status.startsWith('error') ? '#b00020' : '#666', marginBottom:6 }}>
        Status: {status}
      </div>
      <div style={{ marginBottom: 8 }}>
  <button onClick={() => setShowPurchased(p => !p)}>
    {showPurchased ? 'Hide Purchased' : 'Show Purchased'}
  </button>
</div>

      <ul style={{ listStyle:'none', padding:0, margin:0 }}>
        {sortedItems.length === 0 && status === 'ready' && (
          <li style={{ opacity:0.7, padding:'8px 0' }}>No items in list.</li>
        )}
        {sortedItems.map(item => (
          <li key={item.id} style={{ display:'grid', gridTemplateColumns:'1fr 140px auto',
                                     gap:10, alignItems:'center', padding:'10px 0', borderBottom:'1px solid #eee' }}>
            <div style={{ opacity: item.status === 'purchased' ? 0.6 : 1, fontStyle: item.status === 'purchased' ? 'italic' : 'normal' }}>
              <div style={{ fontWeight:600 }}>{item.name}</div>
              <div style={{ fontSize:12, color:'#666' }}>
                {item.status === 'purchased'
                  ? (() => {
                      const n = Math.floor((new Date() - new Date(item.purchasedAt))/86400000);
                      return n <= 0 ? 'purchased today' : n === 1 ? 'purchased 1 day ago' : `purchased ${n} days ago`;
                    })()
                  : 'awaiting purchase'}
              </div>
            </div>
            <div style={{ fontSize:14, textAlign:'right' }}><strong>{item.shop}</strong></div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => togglePurchased(item)}>{item.status === 'purchased' ? 'Undo' : 'Purchase'}</button>
              <button onClick={() => removeItem(item.id)}>{'\u274C'}</button>
            </div>
          </li>
        ))}
      </ul>
      {/* --- Shopping Now confirmation modal --- */}
{confirmOpen && (
  <div
    onClick={cancelShoppingNow}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: '#fff', borderRadius: 12, padding: 16, minWidth: 320,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
      }}
      role="dialog" aria-modal="true" aria-labelledby="shopping-now-title"
    >
      <div id="shopping-now-title" style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
        Send “Shopping now”?
      </div>

      <div style={{ fontSize: 14, color: '#333', lineHeight: 1.4, marginBottom: 12 }}>
        This will notify everyone <em>except</em> you ({me || '—'}).<br/>
        Message preview:
        <div style={{
          marginTop: 8, padding: 8, border: '1px solid #eee', borderRadius: 8,
          background: '#fafafa', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12
        }}>
{`[${(me || '').toUpperCase()}] is shopping — add items to the shopping list now!

https://${window.location.hostname}/`}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={cancelShoppingNow}>Cancel</button>
        <button
          onClick={confirmShoppingNow}
          style={{ background: '#1e63ff', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 8 }}
        >
          Send
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}

/* ---------- Leftovers mini‑app (with inline cooked-date editor) ---------- */

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const cmpByAddedAtDesc = (a, b) =>
  new Date(b.addedAt || b.created) - new Date(a.addedAt || a.created);

function cookedLabel(iso) {
  if (!iso) return '';
  const now = new Date();
  const d = new Date(iso);
  const n = daysBetween(new Date(d.toDateString()), new Date(now.toDateString()));
  if (n <= 0) return 'made today';
  if (n === 1) return 'made yesterday';
  if (n >= 3 && n <= 7) return `made last ${DOW[d.getDay()]}`;
  if (n > 7) return 'made 1 week+ ago';
  return 'made 2 days ago';
}

function ageColor(iso) {
  if (!iso) return undefined;
  const n = daysBetween(new Date(iso), new Date());
  if (n >= 5) return '#d00000';
  if (n >= 3) return '#f7a300';
  return '#666';
}

// menu choices
function buildEditChoices() {
  const today = new Date();
  const choices = [{ label: 'Today', offset: 0 }];
  for (let offset = 1; offset <= 6; offset++) {
    const d = new Date(today);
    d.setDate(today.getDate() - offset);
    const label = offset === 1 ? 'Yesterday' : DOW[d.getDay()];
    choices.push({ label, offset });
  }
  choices.push({ label: '1 week+', offset: 8 }); // store as 8 days ago
  return choices;
}
function dateFromOffset(offsetDays) {
  const base = new Date();
  const d = new Date(base);
  d.setDate(base.getDate() - offsetDays);
  return new Date(d.toDateString()); // midnight local
}

function Leftovers() {
  const [items, setItems] = React.useState([]);
  const [name, setName] = React.useState('');
  const [status, setStatus] = React.useState('boot');

  // inline editor
  const [editingId, setEditingId] = React.useState(null);
  const [customDate, setCustomDate] = React.useState(''); // YYYY-MM-DD

  // keep labels fresh (minute tick + midnight)
  React.useEffect(() => {
    const tick = setInterval(() => setStatus(s => (s === 'ready' ? 'ready ' : 'ready')), 60_000);
    const now = new Date();
    const midnight = new Date(now); midnight.setHours(24, 0, 0, 0);
    const toMidnight = setTimeout(() => setStatus(s => (s === 'ready' ? 'ready ' : 'ready')), midnight - now);
    return () => { clearInterval(tick); clearTimeout(toMidnight); };
  }, []);

  // load + subscribe
  React.useEffect(() => {
    let unsub = () => {};
    (async () => {
      try {
        setStatus('loading…');
        const list = await pb.collection('leftovers').getFullList({ sort: '-created' });
        setItems([...list].sort(cmpByAddedAtDesc));

        unsub = await pb.collection('leftovers').subscribe('*', async () => {
          const refreshed = await pb.collection('leftovers').getFullList({ sort: '-created' });
          setItems([...refreshed].sort(cmpByAddedAtDesc));
        });

        setStatus('ready');
      } catch (e) {
        console.error('[Leftovers] init error:', e);
        setStatus('error: ' + (e?.message || String(e)));
      }
    })();

    return () => { try { unsub(); } catch {} };
  }, []);

  // always render sorted
  const sorted = React.useMemo(() => [...items].sort(cmpByAddedAtDesc), [items]);

  async function addItem() {
    const n = name.trim();
    if (!n) return;
    try {
      setStatus('creating…');
      await pb.collection('leftovers').create({
        name: n,
        addedAt: new Date().toISOString(),
      });
      setName('');
      setStatus('ready');
    } catch (e) {
      console.error('[Leftovers] create error:', e);
      setStatus('error: ' + (e?.message || String(e)));
      alert('Create failed (see console).');
    }
  }

  async function removeItem(id) {
    try { await pb.collection('leftovers').delete(id); }
    catch (e) { console.error('[Leftovers] delete error:', e); }
  }

  const editChoices = React.useMemo(buildEditChoices, []);

  async function applyEdit(id, offsetStr, manualISO) {
    try {
      let newISO;
      if (manualISO) {
        // YYYY-MM-DD -> midnight UTC ISO
        const [y, m, d] = manualISO.split('-').map(Number);
        newISO = new Date(Date.UTC(y, m - 1, d)).toISOString();
      } else {
        const offset = Number(offsetStr);
        newISO = dateFromOffset(offset).toISOString();
      }

      await pb.collection('leftovers').update(id, { addedAt: newISO });

      setItems(prev =>
        prev
          .map(it => (it.id === id ? { ...it, addedAt: newISO } : it))
          .sort(cmpByAddedAtDesc)
      );
      setEditingId(null);
      setCustomDate('');
    } catch (e) {
      console.error('[Leftovers] edit error:', e);
      alert('Update failed.');
    }
  }

  return (
    <div>
      <h2 style={{ marginTop: 0, color: '#c64bf5' }}>leftovers</h2>

      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
        <input
          type="text"
          placeholder="Add a leftover (e.g., Pasta bake)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' ? addItem() : null}
          style={{ flex:1, minWidth:220, padding:8 }}
        />
        <button onClick={addItem}>Add</button>
      </div>

      <div style={{ fontSize:12, color: status.startsWith('error') ? '#b00020' : '#666', marginBottom:6 }}>
        Status: {status}
      </div>

      <ul style={{ listStyle:'none', padding:0, margin:0 }}>
        {sorted.length === 0 && status === 'ready' && (
          <li style={{ opacity:0.7, padding:'8px 0' }}>No leftovers yet.</li>
        )}
        {sorted.map(it => {
          const label = cookedLabel(it.addedAt);
          const color = ageColor(it.addedAt);
          const isEditing = editingId === it.id;

          return (
            <li key={it.id} style={{
              display:'grid', gridTemplateColumns:'1fr auto', gap:10,
              alignItems:'center', padding:'10px 0', borderBottom:'1px solid #eee'
            }}>
              <div>
                <div style={{ fontWeight:600 }}>{it.name}</div>

                {!isEditing ? (
                  <div
                    title="Click to edit cooked date"
                    onClick={() => setEditingId(it.id)}
                    style={{ fontSize:12, color, cursor:'pointer', textDecoration:'underline dotted' }}
                  >
                    {label}
                  </div>
                ) : (
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:4, flexWrap:'wrap' }}>
                    <select
                      autoFocus
                      defaultValue={0}
                      onChange={(e) => applyEdit(it.id, e.target.value, null)}
                      style={{ padding:6 }}
                    >
                      {editChoices.map(c => (
                        <option key={c.label} value={c.offset}>{c.label}</option>
                      ))}
                    </select>

                    <span style={{ fontSize:12, opacity:0.7 }}>or</span>

                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                      style={{ padding:6 }}
                    />
                    <button
                      disabled={!customDate}
                      onClick={() => applyEdit(it.id, null, customDate)}
                      style={{ padding:'6px 10px' }}
                    >
                      Set
                    </button>

                    <button onClick={() => { setEditingId(null); setCustomDate(''); }} style={{ padding:'6px 10px' }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div>
                <button onClick={() => removeItem(it.id)} title="Delete">{'\u274C'}</button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


  


  /* ---------- Chores mini‑app (list/cards + archive modal) ---------- */

  
// ---- Chores date helpers (Saturday-based week; rollover Fri->Sat midnight) ----
const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const DAY_MS = 86400000;

const startOfDay = (d = new Date()) => {
  const date = new Date(d);
  return new Date(date.toDateString()); // local midnight
};

function todayISODate(d = new Date()) {
  const date = startOfDay(d);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// ---------- NEW: Week begins Saturday ----------
function startOfWeekSat(d = new Date()) {
  const s = startOfDay(d);
  const day = s.getDay(); // 0 Sun ... 6 Sat
  const diff = (day - 6 + 7) % 7; // Sat => 0, Sun => 1, Fri => 6
  s.setDate(s.getDate() - diff);
  return s;
}

function firstSaturdayOnOrAfterJan1(year) {
  const d = startOfDay(new Date(year, 0, 1));
  const add = (6 - d.getDay() + 7) % 7; // move forward to Saturday
  d.setDate(d.getDate() + add);
  return d;
}

function weekNumberSat(d = new Date()) {
  const weekStart = startOfWeekSat(d);
  let first = firstSaturdayOnOrAfterJan1(weekStart.getFullYear());
  if (weekStart < first) {
    first = firstSaturdayOnOrAfterJan1(weekStart.getFullYear() - 1);
  }
  const weeksSince = Math.floor((weekStart - first) / (7 * DAY_MS)) + 1;
  return { year: weekStart.getFullYear(), week: weeksSince, weekStart };
}

// ---------- LEGACY: Week begins Sunday (keep for matching old completions) ----------
function startOfWeekSun(d = new Date()) {
  const s = startOfDay(d);
  const day = s.getDay(); // 0 = Sunday
  s.setDate(s.getDate() - day);
  return s;
}

function firstSundayOnOrAfterJan1(year) {
  const d = startOfDay(new Date(year, 0, 1));
  const add = (7 - d.getDay()) % 7;
  d.setDate(d.getDate() + add);
  return d;
}

function weekNumberSun(d = new Date()) {
  const weekStart = startOfWeekSun(d);
  let first = firstSundayOnOrAfterJan1(weekStart.getFullYear());
  if (weekStart < first) {
    first = firstSundayOnOrAfterJan1(weekStart.getFullYear() - 1);
  }
  const weeksSince = Math.floor((weekStart - first) / (7 * DAY_MS)) + 1;
  return { year: weekStart.getFullYear(), week: weeksSince, weekStart };
}
function getRecentPeriods(task, now) {
  const periods = [];
  const d = startOfDay(new Date(now));
  const clone = (date) => new Date(date.getTime());

  switch (task.freq) {
    case "daily": {
      for (let i = 1; i <= 5; i++) {
        const date = clone(d);
        date.setDate(date.getDate() - i);
        periods.push(todayISODate(date));
      }
      break;
    }

    case "weekly": {
      for (let i = 1; i <= 5; i++) {
        const date = clone(d);
        date.setDate(date.getDate() - i * 7);
        periods.push(choresWeekKey(date));
      }
      break;
    }

    case "fortnightly": {
      // include the current fortnight + 4 previous
      const phase =
        task && (task.phase === 1 || task.phase === "1" || task.phase === true)
          ? 1
          : 0;
      for (let i = 0; i < 5; i++) {
        const date = clone(d);
        date.setDate(date.getDate() - i * 14);
        periods.push(choresFortnightKey(date, phase));
      }
      break;
    }

    case "monthly": {
      for (let i = 1; i <= 5; i++) {
        const date = clone(d);
        date.setMonth(date.getMonth() - i);
        periods.push(`${date.getFullYear()}-${pad(date.getMonth() + 1)}`);
      }
      break;
    }

    case "quarterly": {
      for (let i = 1; i <= 5; i++) {
        const date = clone(d);
        date.setMonth(date.getMonth() - i * 3);
        periods.push(choresQuarterKey(date));
      }
      break;
    }

    default:
      break;
  }

  return periods.reverse(); // oldest -> newest (rightmost is most recent)
}

// NEW keys (Saturday week)
export function choresWeekKey(d = new Date()) {
  const { year, week } = weekNumberSat(d);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

export function choresFortnightKey(d = new Date(), phase = 0) {
  const { year, week } = weekNumberSat(d);

  // anchor to the first week number of the fortnight (0/1 parity choice)
  const baseWeek = week - (week % 2);
  const anchorWeek = (baseWeek % 2 === phase) ? baseWeek : baseWeek + 1;

  return `${year}-FN${String(anchorWeek).padStart(2, '0')}`;
}

// LEGACY keys (Sunday week) - only used to match old completions
function choresWeekKeyLegacySun(d = new Date()) {
  const { year, week } = weekNumberSun(d);
  return `${year}-W${String(week).padStart(2, '0')}`;
}
function choresFortnightKeyLegacySun(d = new Date(), phase = 0) {
  const { year, week } = weekNumberSun(d);
  const baseWeek = week - (week % 2);
  const anchorWeek = (baseWeek % 2 === phase) ? baseWeek : baseWeek + 1;
  return `${year}-FN${String(anchorWeek).padStart(2, '0')}`;
}
export function choresQuarterKey(d = new Date()) {
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

export function periodForTask(task, d = new Date()) {
  const date = new Date(d); // force it to be a Date

  console.log('[periodForTask]', {
    task: task.name,
    freq: task.freq,
    phase: task?.phase,
    now: date.toISOString(),
  });

  switch (task.freq) {
    case 'daily':
      return todayISODate(date);
    case 'weekly':
      return choresWeekKey(date);
    case 'fortnightly':
      return choresFortnightKey(date, task?.phase ? 1 : 0);
    case 'monthly':
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
    case 'quarterly':
      return choresQuarterKey(date);
    default:
      return choresWeekKey(date);
  }
}

function periodForTaskLegacySun(task, d = new Date()) {
  const date = new Date(d);
  switch (task.freq) {
    case 'weekly':
      return choresWeekKeyLegacySun(date);
    case 'fortnightly':
      return choresFortnightKeyLegacySun(date, task?.phase ? 1 : 0);
    default:
      return null;
  }
}

function getDueLabel(task, pkey, now = new Date()) {
  if (!['fortnightly', 'monthly', 'quarterly'].includes(task.freq)) return '';

  console.log('getDueLabel called for', task.name, '– "pkey:" –', pkey);

  const end = (() => {
    switch (task.freq) {
      case 'fortnightly': {
        // Extract base week number from the pkey
        const [yearStr, fnStr] = pkey.split('-FN');
        const baseWeek = parseInt(fnStr, 10);
const baseDate = firstSaturdayOnOrAfterJan1(parseInt(yearStr, 10));        baseDate.setDate(baseDate.getDate() + baseWeek * 7);
        const endDate = new Date(baseDate);
        endDate.setDate(endDate.getDate() + 13); // fortnight = 14 days total, so end on day 13
        return endDate;
      }

      case 'monthly': {
        const d = new Date(now.getFullYear(), now.getMonth() + 1, 0); // last day of month
        return d;
      }

      case 'quarterly': {
        const [yearStr, qStr] = pkey.split('-Q');
        const year = parseInt(yearStr, 10);
        const q = parseInt(qStr, 10);
        if (isNaN(year) || isNaN(q) || q < 1 || q > 4) return '';
        const endMonth = q * 3;
        return new Date(year, endMonth, 0); // last day of quarter
      }
    }
  })();

  const daysAway = Math.round((startOfDay(end) - startOfDay(now)) / DAY_MS);

  if (daysAway <= 7) return 'due this week';
  if (daysAway <= 14) return 'due next week';
  if (daysAway <= 21) return 'due in two weeks';
  if (daysAway <= 28) return 'due in three weeks';
  if (daysAway <= 42) return 'due in a month';
  if (daysAway <= 60) return 'due in ~6 weeks';
  if (daysAway <= 90) return 'due this quarter';
  return `due in ${Math.ceil(daysAway / 7)} weeks`;
}

const USERS = ['DB','NM','LB','HB'];

const weekStart = startOfWeekSat(new Date());
const weekBeginningLabel =
  `Week beginning Sat ${weekStart.getDate()} ${weekStart.toLocaleString('default', { month: 'long' })}`;
console.log("🕒 weekStart =", weekStart.toISOString());

function Chores() {
  const [tasks, setTasks] = React.useState([]);
  const [comps, setComps] = React.useState([]);        // recent completions
  const [status, setStatus] = React.useState('boot');
  const [ownerPick, setOwnerPick] = React.useState(null); // { taskId, value }
  const [view, setView] = React.useState('cards'); // 'list' | 'cards'
  const [showArchive, setShowArchive] = React.useState(false);
  const [now, setNow] = React.useState(startOfDay(new Date()));
// ---- History bar: last 5 periods ----
const HistoryBar = ({ task, completions, now }) => {
  const recentPeriods = getRecentPeriods(task, now);

  const statuses = recentPeriods.map((pkey) => {
    // match either period_scope or (older) period_key
    const legacy = periodForTaskLegacySun(task, now);

const match = completions.find(
  (c) =>
    c.taskId === task.id &&
    (c.period_scope === pkey || c.period_key === pkey ||
     (legacy && (c.period_scope === legacy || c.period_key === legacy)))
);
    if (match) return "tick"; // completion takes precedence

    const createdKey = periodForTask(task, task.created);
    if (pkey < createdKey) return "none";
    return "miss";
  });

  const icon = (status, i) => {
    if (status === "tick") return <span key={i}>✅</span>;
    if (status === "miss") return <span key={i}>❌</span>;
    return <span key={i}>➖</span>;
  };

  return <div style={{ fontSize: 14 }}>{statuses.map(icon)}</div>;
};

  React.useEffect(() => {
  const updateNow = () => setNow(startOfDay(new Date()));

  updateNow(); // ensure we start with correct value

  const interval = setInterval(updateNow, 60 * 1000); // check every 60s

  return () => clearInterval(interval);
}, []);

  // bootstrap + realtime
  React.useEffect(() => {
    let unsubs = [];

    (async () => {
      try {
        setStatus('loading…');

        // tasks
        const t = await pb.collection('tasks').getFullList({ sort: 'name' });
        setTasks(t);

        // recent completions (simple: last ~120 days)
        const since = new Date(); since.setDate(since.getDate() - 120);
        const c = await pb.collection('task_completions').getFullList({
          filter: `created >= "${since.toISOString()}"`,
          sort: '-created',
        });
        setComps(c);

        // live updates
        unsubs.push(await pb.collection('tasks').subscribe('*', async () => {
          const t2 = await pb.collection('tasks').getFullList({ sort: 'name' });
          setTasks(t2);
        }));
        unsubs.push(await pb.collection('task_completions').subscribe('*', async () => {
          const since2 = new Date(); since2.setDate(since2.getDate() - 120);
          const c2 = await pb.collection('task_completions').getFullList({
            filter: `created >= "${since2.toISOString()}"`,
            sort: '-created',
          });
          setComps(c2);
        }));

        setStatus('ready');
      } catch (e) {
        console.error('[Chores] init error', e);
        setStatus('error: ' + (e?.message || String(e)));
      }
    })();

    return () => { unsubs.forEach(u => { try { u(); } catch {} }); };
  }, []);

  // build “current period” view
  const periodKeyFor = (task) => periodForTask(task, now);

  
  // quick index of completions this period by (taskId, period_scope)
  console.log('[comps]', comps.map(c => ({
  id: c.id,
  taskId: c.taskId,
  period_scope: c.period_scope,
  key: `${c.taskId}::${c.period_scope}`
})));



// quick index of completions this period by (taskId, period_scope)
const compIndex = React.useMemo(() => {
  const m = new Map();
  comps.forEach(c => {
    // Find the task for this completion
    const task = tasks.find(t => t.id === c.taskId);
    if (!task) return;

    const expectedPkey = periodForTask(task, now);
const legacy = periodForTaskLegacySun(task, now);
if (c.period_scope === expectedPkey || (legacy && c.period_scope === legacy)) {
  // Always index by the NEW expected key
  m.set(`${c.taskId}::${expectedPkey}`, c);
}
  });
  return m;
}, [comps, tasks, now]);
console.log('[debug: completions for W33]', comps
  .filter(c => c.period_scope === '2025-W33')
  .map(c => ({
    id: c.id,
    taskId: c.taskId,
    completed_at: c.completed_at,
    completed_by: c.completed_by
  }))
);

  // bucket rows by freq; order inside a freq = uncompleted then completed
  const bucketed = React.useMemo(() => {
    const b = {
      daily: { uncompleted: [], completed: [] },
      weekly: { uncompleted: [], completed: [] },
      fortnightly: { uncompleted: [], completed: [] },
      quarterly: { uncompleted: [], completed: [] },
      monthly: { uncompleted: [], completed: [] },
    };
    tasks.forEach(task => {
      const pkey = periodKeyFor(task);

      
      console.log('🔍 periodKeyFor', {
  task: task.name,
  freq: task.freq,
  now,
  period: periodForTask(task, now),
});

console.log('🛠 DEBUG: Weekly task completions check');
tasks.forEach(task => {
  if (task.freq !== 'weekly') return;
  const pkey = periodForTask(task, now);
  const compForTask = comps.find(c => c.taskId === task.id);

  console.log('--- Task:', task.name);
  console.log('  Computed period key:', pkey);
  if (compForTask) {
    console.log('  Has completion in state:', compForTask.period_scope);
  } else {
    console.log('  No completion object found for this task');
  }
});

console.log('🧠 current week key is', choresWeekKey(now));
      
      console.log('[bucket]', {
  task: task.name,
  id: task.id,
  pkey,
  compKey: `${task.id}::${pkey}`,
  compExists: compIndex.has(`${task.id}::${pkey}`),
  allKeys: [...compIndex.keys()],
});

      const c = compIndex.get(`${task.id}::${pkey}`);
      const row = { task, completion: c, pkey };
      const bucket = b[task.freq] || b.weekly;
      bucket[c ? 'completed' : 'uncompleted'].push(row);
    });
    return b;
  }, [tasks, compIndex]);

console.log('[bucket]', bucketed.weekly.uncompleted.map(r => ({
  task: r.task.name,
  id: r.task.id,
  pkey: r.pkey,
  compKey: `${r.task.id}::${r.pkey}`
})));


  // click handlers
  function startDone(task) {
    setOwnerPick({ taskId: task.id, value: task.defaultOwner || USERS[0] });
  }

 async function confirmDone() {
  const pick = ownerPick;
  if (!pick) return;
  const task = tasks.find(t => t.id === pick.taskId);
  if (!task) return setOwnerPick(null);

  try {
    const now = new Date();
    const localMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const pkey = periodForTask(task, localMidnight);

    console.log('[confirmDone]', {
      name: task.name,
      freq: task.freq,
      pkey,
      completed_at: now.toISOString(),
    });

    await pb.collection('task_completions').create({
      taskId: task.id,
      period_scope: pkey,
      period_key: pkey,
      completed_by: pick.value,
      completed_at: now.toISOString(),
    });
  } catch (e) {
    console.error('[Chores] complete error', e, e?.data);
    alert('Failed to complete task.');
  } finally {
    setOwnerPick(null);
  }
}

  async function undoDone(row) {
    const comp = row.completion;
    if (!comp) return;
    try {
      await pb.collection('task_completions').delete(comp.id);
    } catch (e) {
      console.error('[Chores] undo error', e);
    }
  }

  // helpers for rendering
  const prettyPeriod = (freq) => {
    switch (freq) {
      case 'daily':       return 'today';
      case 'weekly':      return 'this week';
      case 'fortnightly': return 'this fortnight';
      case 'monthly':     return 'this month';
      case 'quarterly':   return 'this quarter';
      default:            return `this ${freq}`;
    }
  };
  const getCompletionMeta = (c) => {
    if (!c) return { who: '', whenISO: '' };
    const who = c.completed_by ?? c.completedBy ?? '';
    const whenISO = c.completed_at ?? c.completedOn ?? '';
    return { who, whenISO };
  };

  // List-row
  const Row = ({ row }) => {
    const { task, completion } = row;
    const isDone = !!completion;
    const { who, whenISO } = getCompletionMeta(completion);
    const whenText = whenISO ? new Date(whenISO).toLocaleDateString() : '';

    return (
      <li
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 10,
          alignItems: 'center',
          padding: '10px 0',
          borderBottom: '1px solid #eee',
          opacity: isDone ? 0.6 : 1,
          userSelect: 'none',
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontStyle: isDone ? 'italic' : 'normal' }}>
            {task.name}
          </div>

          <div style={{ fontSize: 12, color: '#666', fontStyle: isDone ? 'italic' : 'normal' }}>
            {isDone
              ? `completed${who ? ` by ${who}` : ''}${whenText ? ` on ${whenText}` : ''}`
              : `uncompleted • ${prettyPeriod(task.freq)}`}
          </div>
        </div>

        <div>
          {isDone ? (
            <button title="Undo completion" onClick={() => undoDone(row)}>
              Undo
            </button>
          ) : (
            <button title="Mark as done" onClick={() => startDone(task)}>
              Done
            </button>
          )}
        </div>
      </li>
    );
  };

  // Card view
// Card view with full-bleed background image
const Card = ({ row }) => {
  const { task, completion } = row;
  const isDone = !!completion;

  const { who, whenISO } = getCompletionMeta(completion);
  const whenText = whenISO ? new Date(whenISO).toLocaleDateString() : "";

  // Pick background image per state
  // Use your actual assets here:
  const bgUrl = isDone ? "/images/card-done_2.png" : "/images/card-todo_2.png";
  // If you prefer SVGs, PNGs, etc., just point to those files.

  // Border color mirrors state
  const borderColor = isDone ? "#8fd694" : "#ff5c5c";

  // You can tune the overlay tint per state by CSS variable values:
  const tintVars = isDone
    ? { "--card-tint-a": 0.55, "--card-tint-b": 0.35 } // slightly clearer when done
    : { "--card-tint-a": 0.70, "--card-tint-b": 0.50 }; // stronger tint for todo

  return (
    <div
      className="card"
      style={{
        ...tintVars,
        border: `3px solid ${borderColor}`,
        backgroundImage: `url(${bgUrl})`
      }}
    >
      <div
        className="card-title"
        style={{ fontStyle: isDone ? "italic" : "normal" }}
      >
        {task.name}
      </div>

      <div className="card-meta">
        {isDone
          ? `completed${who ? ` by ${who}` : ""}${whenText ? ` on ${whenText}` : ""}`
          : `uncompleted${
              ["fortnightly", "monthly", "quarterly"].includes(task.freq)
                ? ` • ${getDueLabel(task, row.pkey, now)}`
                : ""
            }`}
      </div>

      <div className="card-history">
        <HistoryBar task={task} completions={comps} now={now} />
      </div>

      <div className="card-footer">{task.freq}</div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        {isDone ? (
          <button onClick={() => undoDone(row)}>Undo</button>
        ) : (
          <button onClick={() => startDone(task)}>Done</button>
        )}
      </div>
    </div>
  );
};

  // renderers

  // --- cards view grouped by frequency (inside Chores) ---
  const renderCards = () => {
  // helper to render one frequency block with a heading + its cards
  const renderFreq = (freq, label) => {
    const rows = [
      ...bucketed[freq].uncompleted,
      ...bucketed[freq].completed,
    ];

    if (rows.length === 0) return null; // skip empty sections

    return (
      <section key={freq} style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, margin: '8px 2px' }}>
          {label}
        </div>

 <div className="cards-grid">
  {rows.map((row) => (
    <Card key={`${row.task.id}::${row.pkey}`} row={row} />
  ))}
</div>     </section>
    );
  };

  return (
    <div>
      {renderFreq('daily',       'Daily')}
      {renderFreq('weekly',      'Weekly')}
      {renderFreq('fortnightly', 'Fortnightly')}
      {renderFreq('monthly',     'Monthly')}
      {renderFreq('quarterly',   'Quarterly')}
    </div>
  );
};
  const renderBucketList = (freq) => {
    const b = bucketed[freq];
    const order = [...b.uncompleted, ...b.completed];
    return order.map((row) => <Row key={`${row.task.id}::${row.pkey}`} row={row} />);
  };

  

  return (
    <div>
      {/* header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <h2 style={{ marginTop: 0, color: '#55a630' }}>chores</h2>

<div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
  {weekBeginningLabel}
</div>

<div style={{ fontSize:12, color: status.startsWith('error') ? '#b00020' : '#666', marginBottom: 6 }}>
  Status: {status}
</div>
<div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <div
  style={{
    display: 'inline-flex',
    overflow: 'hidden',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: 2
  }}
>
  <button
    onClick={() => setView('list')}
    style={{
      padding: '6px 10px',
      border: 'none',
      borderRadius: 6,
      background: view === 'list' ? '#e6f5e6' : 'transparent',
      cursor: 'pointer'
    }}
    title="List view"
  >
    List
  </button>
  <button
    onClick={() => setView('cards')}
    style={{
      padding: '6px 10px',
      border: 'none',
      borderRadius: 6,
      background: view === 'cards' ? '#e6f5e6' : 'transparent',
      cursor: 'pointer'
    }}
    title="Card view"
  >
    Cards
  </button>
</div>

          <button
            onClick={() => setShowArchive(true)}
            style={{ padding:'6px 12px', borderRadius: 16, border:'2px solid #ffa94d', background:'#fff8ed' }}
            title="Open archive"
          >
            archive
          </button>
        </div>
      </div>

      <div style={{ fontSize:12, color: status.startsWith('error') ? '#b00020' : '#666', margin: '6px 0 10px' }}>
        Status: {status}
      </div>

      {view === 'list' ? (
        <ul style={{ listStyle:'none', padding:0, margin:0 }}>
          {renderBucketList('daily')}
          {renderBucketList('weekly')}
          {renderBucketList('fortnightly')}
          {renderBucketList('monthly')}
          {renderBucketList('quarterly')}
        </ul>
      ) : (
        renderCards()
      )}

      {/* owner picker */}
      {ownerPick && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.35)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex: 50
        }}>
          <div style={{ background:'#fff', padding:16, borderRadius:8, minWidth:280 }}>
            <div style={{ fontWeight:700, marginBottom:8 }}>Mark task as done</div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <span>Completed by:</span>
              <select
                value={ownerPick.value}
                onChange={(e)=>setOwnerPick({ ...ownerPick, value: e.target.value })}
              >
                {USERS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:12, justifyContent:'flex-end' }}>
              <button onClick={()=>setOwnerPick(null)}>Cancel</button>
              <button onClick={confirmDone}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* archive modal (reuses your existing <Archive /> component) */}
      {showArchive && (
        <div
          onClick={() => setShowArchive(false)}
          style={{
            position:'fixed', inset:0, background:'rgba(0,0,0,0.4)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:60
          }}
        >
          <div
            onClick={(e)=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:12, padding:16, width:'min(100%, 980px)', maxHeight:'90vh', overflow:'auto' }}
          >
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <h3 style={{ margin:0, color:'#ff8c1a' }}>archive</h3>
              <button onClick={()=>setShowArchive(false)}>Close</button>
            </div>
            {/* if you kept your previous Archive component in the file: */}
            <Archive />
          </div>
        </div>
      )}
    </div>
  );
}


// ---------- Archive (PB-driven, no CHORES_TASKS) ----------
function Archive() {
  const [freq, setFreq] = React.useState('all');     // 'all' | 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly'
  const [periodQuery, setPeriodQuery] = React.useState(''); // substring match on period_key
  const [rows, setRows] = React.useState([]);
  const [status, setStatus] = React.useState('boot');

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setStatus('loading…');

        // 1) load tasks (for names/freq/owner)
        const tasks = await pb.collection('tasks').getFullList({ sort: 'name' });
        const byId = new Map(tasks.map(t => [t.id, t])); // quick join map

        // 2) load completions (limit window to something sensible; adjust as you like)
        const since = new Date();
        since.setFullYear(since.getFullYear() - 1); // last 12 months
        const comps = await pb.collection('task_completions').getFullList({
          filter: `created >= "${since.toISOString()}"`,
          sort: '-created',
        });

        // 3) join & shape
        const joined = comps
          .map(c => {
            const t = byId.get(c.taskId);
            if (!t) return null; // task was deleted?
            return {
              periodKey: c.period_key || '',
              freq: (t.freq || '').toLowerCase(),          // daily/weekly/fortnightly/monthly/quarterly
              task: t.name || '(unknown task)',
              owner: t.defaultOwner || '—',
              status: 'completed',                          // PB only stores completions here
              completedBy: c.completed_by || '—',
              completedOn: c.completed_at
                ? new Date(c.completed_at)
                : (c.created ? new Date(c.created) : null),
              sortKey: c.period_key || (c.completed_at || c.created || ''),
            };
          })
          .filter(Boolean)
          // newest-first; if same period_key, use completedOn desc
          .sort((a, b) => {
            if (a.sortKey === b.sortKey) {
              const at = a.completedOn ? a.completedOn.getTime() : 0;
              const bt = b.completedOn ? b.completedOn.getTime() : 0;
              return bt - at;
            }
            return a.sortKey < b.sortKey ? 1 : -1;
          });

        if (!alive) return;
        setRows(joined);
        setStatus('ready');
      } catch (e) {
        console.error('[Archive] load error:', e);
        if (!alive) return;
        setStatus('error: ' + (e?.message || String(e)));
      }
    })();

    return () => { alive = false; };
  }, []);

  // filter
  const filtered = rows.filter(r => {
    if (freq !== 'all' && r.freq !== freq) return false;
    if (periodQuery && !r.periodKey.toLowerCase().includes(periodQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <h2 style={{ marginTop: 0, color: '#ff8c1a' }}>archive</h2>

      <div style={{ fontSize:12, color: status.startsWith('error') ? '#b00020' : '#666', marginBottom: 6 }}>
        Status: {status}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <label>
          <span style={{ fontSize: 12, marginRight: 6 }}>Frequency</span>
          <select value={freq} onChange={e => setFreq(e.target.value)} style={{ padding: 6 }}>
            <option value="all">All</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="fortnightly">Fortnightly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </label>

        <input
          type="text"
          placeholder="Filter by period key… (e.g., 2025-W, 2025-FN14, 2025-Q3)"
          value={periodQuery}
          onChange={e => setPeriodQuery(e.target.value)}
          style={{ flex: 1, minWidth: 240, padding: 8 }}
        />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
              <th style={{ padding: '8px 6px' }}>Period</th>
              <th style={{ padding: '8px 6px' }}>Freq</th>
              <th style={{ padding: '8px 6px' }}>Task</th>
              <th style={{ padding: '8px 6px' }}>Owner</th>
              <th style={{ padding: '8px 6px' }}>Completed by</th>
              <th style={{ padding: '8px 6px' }}>Completed on</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 10, opacity: 0.7 }}>
                  No archived entries match your filters.
                </td>
              </tr>
            ) : (
              filtered.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>{r.periodKey}</td>
                  <td style={{ padding: '8px 6px', textTransform: 'capitalize' }}>{r.freq || '—'}</td>
                  <td style={{ padding: '8px 6px' }}>{r.task}</td>
                  <td style={{ padding: '8px 6px' }}>{r.owner || '—'}</td>
                  <td style={{ padding: '8px 6px' }}>{r.completedBy || '—'}</td>
                  <td style={{ padding: '8px 6px' }}>
                    {r.completedOn ? r.completedOn.toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- App shell ---------- */
export default function App() {
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => {
    if (window.location.search.includes('seed=1')) {
      seedTasksIfNeeded().catch(console.error);
    }
  }, []);

  return (
    <>
      <style>{responsiveCSS}</style>

      <div className="frameShell" style={frame}>
        <section style={areas.shopping}><ShoppingList /></section>
        <section style={areas.leftovers}><Leftovers /></section>

        <section style={areas.chores}>
          <Chores />
        </section>
      </div>

      {/* Archive modal */}
      <Modal open={showArchive} onClose={() => setShowArchive(false)} title="Archive" >
        <Archive />
      </Modal>
    </>
  );
}